import {
  ChangeDetectionStrategy,
  Component,
  effect,
  inject,
  input,
  output,
  signal,
  untracked,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { RouterLink } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import {
  ApiClient,
  type ProductDetailResponse,
  type ProductExtraDto,
} from '../core/api/api.client';
import { AuthService } from '../core/auth/auth.service';
import { CartService } from '../core/cart/cart.service';
import { effectivePriceCents } from '../core/domain/catalog';
import { formatExtraSurcharge } from '../core/domain/product';
import {
  formatQuantity,
  unitPriceWithBreaks,
  type QuantityBreak,
} from '../core/domain/product-quantity';
import { quantityStep } from '../core/domain/order';
import { formatEurFromCents } from '../core/domain/tiers';
import { CtButton } from './button.directive';
import { CtField } from './field';

/**
 * Ajout rapide au panier depuis une liste : quantité, extras, sans ouvrir la fiche.
 */
@Component({
  selector: 'ct-quick-add',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [FormsModule, RouterLink, CtButton, CtField],
  template: `
    <div class="panel">
      @if (loading()) {
        <p class="ct-muted small">Chargement…</p>
      } @else if (error(); as message) {
        <p class="ct-error small">{{ message }}</p>
        <a class="detail" [routerLink]="detailLink()">Voir la fiche</a>
      } @else if (detail(); as page) {
        <form class="form" (ngSubmit)="addToCart()">
          <ct-field label="Quantité" [hint]="quantityHint()">
            <div class="qty">
              <button type="button" class="step" (click)="bump(-1)" [disabled]="busy()" aria-label="Diminuer">
                −
              </button>
              <input
                type="number"
                name="quantity"
                [step]="step()"
                [min]="minQty()"
                [attr.max]="orderMax()"
                [(ngModel)]="quantity"
                required
              />
              <button type="button" class="step" (click)="bump(1)" [disabled]="busy()" aria-label="Augmenter">
                +
              </button>
            </div>
          </ct-field>

          @if (page.product.extras && page.product.extras.length > 0) {
            <fieldset class="extras">
              <legend>Services</legend>
              @for (extra of page.product.extras; track extra.id) {
                <label class="extra">
                  <input
                    type="checkbox"
                    [name]="'extra-' + extra.id"
                    [ngModel]="isExtraSelected(extra.id)"
                    (ngModelChange)="toggleExtra(extra.id, $event)"
                  />
                  <span>{{ extra.label }} · {{ extraLabel(extra) }}</span>
                </label>
              }
            </fieldset>
          }

          <p class="total">Total : <strong>{{ totalLabel() }}</strong></p>

          @if (formError(); as err) {
            <p class="ct-error small" role="alert">{{ err }}</p>
          }
          @if (addedNotice(); as notice) {
            <p class="ok small" role="status">{{ notice }}</p>
          }

          <div class="actions">
            <button type="submit" ctButton="primary" [disabled]="busy()">
              {{ busy() ? 'Ajout…' : 'Ajouter au panier' }}
            </button>
            <a class="detail" [routerLink]="detailLink()">Voir la fiche</a>
          </div>
        </form>
      }
    </div>
  `,
  styles: `
    .panel {
      margin-top: var(--ct-space-2);
      padding: var(--ct-space-2);
      border: 1px solid var(--ct-border-subtle);
      border-radius: var(--ct-radius-md);
      background: var(--ct-kraft);
    }

    .form {
      display: flex;
      flex-direction: column;
      gap: var(--ct-space-2);
    }

    .qty {
      display: flex;
      align-items: center;
      gap: var(--ct-space-1);
    }

    .qty input {
      width: 4.5rem;
      text-align: center;
    }

    .step {
      width: 2rem;
      height: 2rem;
      border: 1px solid var(--ct-border);
      border-radius: var(--ct-radius-sm);
      background: var(--ct-white);
      font: inherit;
      font-size: 1.1rem;
      line-height: 1;
      cursor: pointer;
    }

    .step:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }

    .extras {
      border: 0;
      margin: 0;
      padding: 0;
      display: flex;
      flex-direction: column;
      gap: 0.35rem;
    }

    legend {
      font-size: var(--ct-size-sm);
      font-weight: var(--ct-weight-bold);
      margin-bottom: 0.25rem;
    }

    .extra {
      display: flex;
      gap: var(--ct-space-2);
      align-items: flex-start;
      font-size: var(--ct-size-sm);
      cursor: pointer;
    }

    .total {
      margin: 0;
      font-size: var(--ct-size-sm);
    }

    .actions {
      display: flex;
      flex-wrap: wrap;
      align-items: center;
      gap: var(--ct-space-2);
    }

    .detail {
      font-size: var(--ct-size-sm);
      font-weight: var(--ct-weight-bold);
      color: var(--ct-vert-cageot);
      text-decoration: none;
    }

    .detail:hover {
      text-decoration: underline;
    }

    .ok {
      margin: 0;
      color: var(--ct-vert-cageot);
    }

    .small {
      font-size: var(--ct-size-sm);
      margin: 0;
    }
  `,
})
export class CtQuickAdd {
  readonly producerId = input.required<string>();
  readonly producerName = input.required<string>();
  readonly commune = input.required<string>();
  readonly cardAccepted = input.required<boolean>();
  readonly productId = input.required<string>();

  /** Émis après un ajout réussi — le parent referme le panneau. */
  readonly added = output<void>();

  private readonly api = inject(ApiClient);
  private readonly auth = inject(AuthService);
  private readonly cart = inject(CartService);

  protected readonly loading = signal(true);
  protected readonly error = signal<string | undefined>(undefined);
  protected readonly detail = signal<ProductDetailResponse | undefined>(undefined);
  protected readonly busy = signal(false);
  protected readonly formError = signal<string | undefined>(undefined);
  protected readonly addedNotice = signal<string | undefined>(undefined);

  protected quantity = 1;
  protected selectedExtraIds: string[] = [];

  constructor() {
    effect(() => {
      const producerId = this.producerId();
      const productId = this.productId();
      untracked(() => {
        void this.load(producerId, productId);
      });
    });
  }

  protected detailLink(): string[] {
    return ['/producteur', this.producerId(), 'produit', this.productId()];
  }

  protected step(): number {
    const page = this.detail();
    return quantityStep(page?.product.priceType ?? 'piece', page?.product.quantityStep);
  }

  protected minQty(): number {
    return this.detail()?.product.minQuantity ?? this.step();
  }

  protected breaks(): readonly QuantityBreak[] {
    return this.detail()?.product.quantityBreaks ?? [];
  }

  protected quantityHint(): string {
    const page = this.detail();
    const unit = page?.product.unit ?? '';
    const step = this.step();
    const min = this.minQty();
    const pas = `par pas de ${formatQuantity(step)} ${unit}`;
    if (min > step + 1e-9) {
      return `minimum ${formatQuantity(min)} ${unit} · ${pas}`;
    }
    return pas;
  }

  protected totalLabel(): string {
    if (!this.detail()) {
      return '';
    }
    return formatEurFromCents(Math.round(this.quantity * this.effectiveUnitCents()));
  }

  protected extraLabel(extra: ProductExtraDto): string {
    const unit = this.detail()?.product.unit ?? '';
    return formatExtraSurcharge(extra.priceCents, unit);
  }

  protected isExtraSelected(id: string): boolean {
    return this.selectedExtraIds.includes(id);
  }

  protected toggleExtra(id: string, checked: boolean): void {
    if (checked) {
      if (!this.selectedExtraIds.includes(id)) {
        this.selectedExtraIds = [...this.selectedExtraIds, id];
      }
      return;
    }
    this.selectedExtraIds = this.selectedExtraIds.filter((entry) => entry !== id);
  }

  protected bump(direction: -1 | 1): void {
    const step = this.step();
    const min = this.minQty();
    const max = this.orderMax() ?? Number.POSITIVE_INFINITY;
    const next = Math.round((this.quantity + direction * step) * 1000) / 1000;
    this.quantity = Math.min(max, Math.max(min, next));
  }

  protected orderMax(): number | null {
    const page = this.detail();
    if (page === undefined || page.product.unlimitedStock) {
      return null;
    }
    return page.product.quantity;
  }

  protected addToCart(): void {
    const page = this.detail();
    if (!page || this.busy()) {
      return;
    }

    this.busy.set(true);
    this.formError.set(undefined);
    this.addedNotice.set(undefined);

    const extras = (page.product.extras ?? []).filter((extra) =>
      this.selectedExtraIds.includes(extra.id),
    );
    const extrasLabel = extras.length > 0 ? extras.map((extra) => extra.label).join(', ') : null;
    const extraCents = this.selectedExtrasCents();
    const baseUnitPriceCents = this.baseUnitCents();
    const unitPriceCents = this.effectiveUnitCents();

    try {
      const result = this.cart.add({
        producerId: this.producerId(),
        producerName: this.producerName(),
        commune: this.commune(),
        cardAccepted: this.cardAccepted(),
        productId: page.product.id,
        productName: page.product.name,
        quantity: this.quantity,
        priceType: page.product.priceType,
        unit: page.product.unit,
        unitPriceCents,
        antiWastePercent: page.product.antiWastePercent,
        extraIds: [...this.selectedExtraIds],
        extrasLabel,
        extraCents,
        baseUnitPriceCents,
        quantityStep: this.step(),
        minQuantity: this.minQty(),
        quantityBreaks: this.breaks(),
      });
      this.addedNotice.set(
        result === 'merged' ? 'Quantité mise à jour dans le panier.' : 'Produit ajouté au panier.',
      );
      this.added.emit();
    } catch {
      this.formError.set("Impossible d'ajouter au panier.");
    } finally {
      this.busy.set(false);
    }
  }

  private selectedExtrasCents(): number {
    const extras = this.detail()?.product.extras ?? [];
    return extras
      .filter((extra) => this.selectedExtraIds.includes(extra.id))
      .reduce((sum, extra) => sum + extra.priceCents, 0);
  }

  private baseUnitCents(): number {
    const page = this.detail();
    if (!page) {
      return 0;
    }
    return effectivePriceCents({
      id: page.product.id,
      name: page.product.name,
      category: page.product.category,
      priceCents: page.product.priceCents,
      unit: page.product.unit,
      antiWastePercent: page.product.antiWastePercent,
    });
  }

  private effectiveUnitCents(): number {
    return unitPriceWithBreaks(this.baseUnitCents(), this.quantity, this.breaks()) + this.selectedExtrasCents();
  }

  private async load(producerId: string, productId: string): Promise<void> {
    this.loading.set(true);
    this.error.set(undefined);
    this.addedNotice.set(undefined);
    this.formError.set(undefined);
    this.detail.set(undefined);
    this.selectedExtraIds = [];
    try {
      const page = this.auth.isAuthenticated()
        ? await firstValueFrom(this.api.meProductDetail(producerId, productId))
        : await firstValueFrom(this.api.productDetail(producerId, productId));
      this.detail.set(page);
      this.quantity =
        page.product.minQuantity ?? quantityStep(page.product.priceType, page.product.quantityStep);
    } catch {
      this.error.set('Produit introuvable ou indisponible.');
    } finally {
      this.loading.set(false);
    }
  }
}
